import React from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { useAuth } from '../App';
import { apiRequest } from '../api';

interface Customer {
  id: number;
  id_login: number;
  firma: string;
  vorname: string;
  nachname: string;
  str: string;
  plz: string;
  ort: string;
  land: string;
  tel: string;
  fax: string;
  email: string;
}

const emptyCustomer: Customer = {
  id: 0, id_login: 0, firma: '', vorname: '', nachname: '', str: '', plz: '', ort: '', land: 'de', tel: '', fax: '', email: '',
};

const europeanCountries = [
  { code: 'al', name: 'Albanien' },
  { code: 'ad', name: 'Andorra' },
  { code: 'am', name: 'Armenien' },
  { code: 'az', name: 'Aserbaidschan' },
  { code: 'be', name: 'Belgien' },
  { code: 'ba', name: 'Bosnien und Herzegowina' },
  { code: 'bg', name: 'Bulgarien' },
  { code: 'dk', name: 'Dänemark' },
  { code: 'de', name: 'Deutschland' },
  { code: 'ee', name: 'Estland' },
  { code: 'fi', name: 'Finnland' },
  { code: 'fr', name: 'Frankreich' },
  { code: 'ge', name: 'Georgien' },
  { code: 'gr', name: 'Griechenland' },
  { code: 'ie', name: 'Irland' },
  { code: 'is', name: 'Island' },
  { code: 'it', name: 'Italien' },
  { code: 'kz', name: 'Kasachstan' },
  { code: 'xk', name: 'Kosovo' },
  { code: 'hr', name: 'Kroatien' },
  { code: 'lv', name: 'Lettland' },
  { code: 'li', name: 'Liechtenstein' },
  { code: 'lt', name: 'Litauen' },
  { code: 'lu', name: 'Luxemburg' },
  { code: 'mt', name: 'Malta' },
  { code: 'md', name: 'Moldau' },
  { code: 'mc', name: 'Monaco' },
  { code: 'me', name: 'Montenegro' },
  { code: 'nl', name: 'Niederlande' },
  { code: 'mk', name: 'Nordmazedonien' },
  { code: 'no', name: 'Norwegen' },
  { code: 'at', name: 'Österreich' },
  { code: 'pl', name: 'Polen' },
  { code: 'pt', name: 'Portugal' },
  { code: 'ro', name: 'Rumänien' },
  { code: 'ru', name: 'Russland' },
  { code: 'sm', name: 'San Marino' },
  { code: 'se', name: 'Schweden' },
  { code: 'ch', name: 'Schweiz' },
  { code: 'rs', name: 'Serbien' },
  { code: 'sk', name: 'Slowakei' },
  { code: 'si', name: 'Slowenien' },
  { code: 'es', name: 'Spanien' },
  { code: 'cz', name: 'Tschechien' },
  { code: 'tr', name: 'Türkei' },
  { code: 'ua', name: 'Ukraine' },
  { code: 'hu', name: 'Ungarn' },
  { code: 'va', name: 'Vatikanstadt' },
  { code: 'gb', name: 'Vereinigtes Königreich' },
  { code: 'by', name: 'Weißrussland' },
  { code: 'cy', name: 'Zypern' },
];

const normalizeCustomer = (customer: Customer): Customer => ({
  ...customer,
  land: customer.land?.trim().toLowerCase() || 'de',
});

const StammdatenPage: React.FC = () => {
  const { token, logout } = useAuth();
  const [customer, setCustomer] = React.useState<Customer>(emptyCustomer);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [dialogAction, setDialogAction] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  React.useEffect(() => {
    if (!token) return;
    apiRequest<Customer>('/account/customer', token)
      .then((loaded) => setCustomer(normalizeCustomer(loaded)))
      .catch((requestError: Error) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [token]);

  const updateField = (field: keyof Customer, value: string) => {
    setCustomer((current) => ({ ...current, [field]: value }));
    setSuccess(null);
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await apiRequest<Customer>('/account/customer', token, {
        method: 'PUT',
        body: JSON.stringify(customer),
      });
      setCustomer(normalizeCustomer(updated));
      setSuccess('Ihre Stammdaten wurden gespeichert.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDialog = (action: string) => {
    setDialogAction(action);
  };

  const handleDialogConfirm = async () => {
    if (!token) return;
    if (dialogAction !== 'Account löschen') {
      setDialogAction(null);
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await apiRequest<{ message: string }>('/account', token, { method: 'DELETE' });
      logout();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Account konnte nicht gelöscht werden.');
      setDialogAction(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom>Stammdaten</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Prüfen und aktualisieren Sie Ihre Kontakt- und Rechnungsanschrift.</Typography>
      <Paper component="form" onSubmit={handleSave} elevation={0} sx={{ p: { xs: 2, sm: 4 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box> : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={600}>Kontakt</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Kundennummer</Typography>
                <Typography variant="body1" fontWeight={500}>{customer.id_login}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight={500}>{[customer.vorname, customer.nachname].filter(Boolean).join(' ')}</Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField required fullWidth label="E-Mail-Adresse" type="email" value={customer.email} onChange={(event) => updateField('email', event.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Telefon (optional)" type="tel" value={customer.tel} onChange={(event) => updateField('tel', event.target.value)} />
              </Grid>
              <Grid item xs={12} sx={{ pt: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>Anschrift</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Firma (optional)" value={customer.firma} onChange={(event) => updateField('firma', event.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField required fullWidth label="Straße und Hausnummer" value={customer.str} onChange={(event) => updateField('str', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField required fullWidth label="PLZ" value={customer.plz} onChange={(event) => updateField('plz', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField required fullWidth label="Ort" value={customer.ort} onChange={(event) => updateField('ort', event.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Land" value={customer.land} onChange={(event) => updateField('land', event.target.value)}>
                  {europeanCountries.map((country) => (
                    <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Button type="submit" variant="contained" disabled={saving} sx={{ mt: 1 }}>
                  {saving ? 'Wird gespeichert …' : 'Änderungen speichern'}
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
      <Grid container spacing={2.5} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              bgcolor: '#ffebee',
              border: '2px solid',
              borderColor: 'error.main',
              color: 'black'
            }}
          >
            <Typography variant="h6" sx={{ color: 'error.dark' }} gutterBottom>
              Account kündigen
            </Typography>
            <Typography variant="body1" sx={{ color: 'error.dark', mb: 2 }}>
              <strong>Warnung:</strong> Durch die Kündigung Ihres Accounts werden alle Ihre Produkte, persönlichen Daten und Einstellungen <strong>unwiderruflich gelöscht</strong>. Dieser Vorgang kann nicht rückgängig gemacht werden.
            </Typography>
            <Button variant="contained" color="error" onClick={() => handleOpenDialog('Account löschen')}>
              Account dauerhaft löschen
            </Button>
          </Paper>
        </Grid>
      </Grid>
      <Dialog open={Boolean(dialogAction)} onClose={() => !deleting && setDialogAction(null)}>
        <DialogTitle>{dialogAction}?</DialogTitle>
        <DialogContent>
          <Typography>
            Alle Ihre persönlichen Daten und Einstellungen werden unwiderruflich gelöscht. Dieser Vorgang kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAction(null)} disabled={deleting}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={handleDialogConfirm} disabled={deleting}>
            {deleting ? 'Wird gelöscht …' : 'Endgültig löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StammdatenPage;
